import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type {
  Bid,
  BidLeveling,
  BidPackage,
  BidSolicitation,
  Project,
  Sub,
} from '../types'
import { fmtDate, fmtMoney, todayISO } from '../lib/format'
import Badge from '../components/Badge'

type LevelingResultUI = {
  scope_matrix?: Array<{
    scope_item: string
    coverage: Array<{ bid_id: string; included: boolean; note: string | null }>
  }>
  apples_to_apples?: Array<{
    bid_id: string
    sub_name: string
    raw_amount: number
    adjusted_amount: number
    adjustment_reasoning: string
  }>
  scope_gaps_against_master_scope?: Array<{ item_description: string; why_no_one_bid_it: string }>
  exclusions_that_become_change_orders?: Array<{
    bid_id: string
    item: string
    estimated_change_order: number | null
  }>
  recommendation?: { recommended_bid_id: string | null; reasoning: string; risks: string[] }
}

export default function BidsTab({ project }: { project: Project }) {
  const [bids, setBids] = useState<Bid[]>([])
  const [subs, setSubs] = useState<Sub[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<Bid>>({ bid_date: todayISO() })

  // v2 Module 3: bid packages lifecycle
  const [packages, setPackages] = useState<Array<BidPackage & { solicitations?: number; received?: number; total_bids_for_trade?: number }>>([])
  const [pkgError, setPkgError] = useState<string | null>(null)
  const [pkgGenerating, setPkgGenerating] = useState(false)
  const [pkgTrade, setPkgTrade] = useState('')
  const [pkgDeadline, setPkgDeadline] = useState('')
  const [activePkg, setActivePkg] = useState<BidPackage | null>(null)
  const [activeSolicitations, setActiveSolicitations] = useState<BidSolicitation[]>([])
  const [activeLeveling, setActiveLeveling] = useState<BidLeveling | null>(null)
  const [pkgBusy, setPkgBusy] = useState<string | null>(null)
  const [sendSubIds, setSendSubIds] = useState<string[]>([])

  useEffect(() => {
    void api.bids.list(project.id).then(setBids)
    void api.subs.list().then(setSubs)
    void refreshPackages()
  }, [project.id])

  async function refreshPackages() {
    try {
      const list = await api.bidPackages.list(project.id)
      setPackages(list as typeof packages)
    } catch (e) {
      setPkgError((e as Error).message)
    }
  }
  async function pickPackage(pkgId: string) {
    setActivePkg(null)
    setActiveSolicitations([])
    setActiveLeveling(null)
    setSendSubIds([])
    try {
      const data = await api.bidPackages.get(pkgId)
      setActivePkg(data.bid_package)
      setActiveSolicitations(data.solicitations)
      setActiveLeveling(data.leveling)
    } catch (e) {
      setPkgError((e as Error).message)
    }
  }
  async function generatePackage() {
    if (!pkgTrade.trim()) {
      setPkgError('Pick a trade first.')
      return
    }
    setPkgGenerating(true)
    setPkgError(null)
    try {
      await api.bidPackages.generate(project.id, pkgTrade.trim(), pkgDeadline || undefined)
      setPkgTrade('')
      setPkgDeadline('')
      await refreshPackages()
    } catch (e) {
      setPkgError((e as Error).message)
    } finally {
      setPkgGenerating(false)
    }
  }
  async function sendPackage() {
    if (!activePkg || sendSubIds.length === 0) return
    setPkgBusy('send')
    try {
      const r = await api.bidPackages.send(activePkg.id, { sub_ids: sendSubIds, response_deadline: activePkg.response_deadline ?? undefined })
      alert(`Sent ${r.sent}, failed ${r.failed}.${r.failures.length ? '\n' + r.failures.map((f) => `${f.sub_id}: ${f.error}`).join('\n') : ''}`)
      await pickPackage(activePkg.id)
      await refreshPackages()
    } catch (e) {
      setPkgError((e as Error).message)
    } finally {
      setPkgBusy(null)
    }
  }
  async function levelPackage() {
    if (!activePkg) return
    setPkgBusy('level')
    try {
      const r = await api.bidPackages.level(activePkg.id)
      setActiveLeveling(r.leveling)
      await refreshPackages()
    } catch (e) {
      setPkgError((e as Error).message)
    } finally {
      setPkgBusy(null)
    }
  }
  async function awardBid(bidId: string) {
    if (!activePkg) return
    if (!confirm('Award this bid? This will draft a sub agreement and unselect other bids in the trade.')) return
    setPkgBusy('award')
    try {
      const r = await api.bidPackages.award(activePkg.id, bidId)
      alert(`Awarded. Sub agreement drafted (id ${r.sub_agreement.id}). Open the Subs tab to send for signature.`)
      setActivePkg(r.bid_package)
      await refreshPackages()
      await api.bids.list(project.id).then(setBids)
    } catch (e) {
      setPkgError((e as Error).message)
    } finally {
      setPkgBusy(null)
    }
  }

  // Group by trade for side-by-side leveling
  const byTrade = useMemo(() => {
    const map = new Map<string, Bid[]>()
    for (const b of bids) {
      const arr = map.get(b.trade) ?? []
      arr.push(b)
      map.set(b.trade, arr)
    }
    // Sort each trade by amount ascending
    for (const arr of map.values()) arr.sort((a, b) => a.bid_amount - b.bid_amount)
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [bids])

  async function add() {
    if (!draft.trade || draft.bid_amount === undefined) return
    const bid = await api.bids.create(project.id, draft)
    setBids((p) => [...p, bid])
    setAdding(false)
    setDraft({ bid_date: todayISO() })
  }

  async function setSelected(bid: Bid) {
    if (!window.confirm(`Mark this bid as selected for ${bid.trade}?`)) return
    // Unselect other bids in same trade first
    const others = bids.filter((b) => b.trade === bid.trade && b.selected === 1 && b.id !== bid.id)
    for (const o of others) {
      await api.bids.update(o.id, { selected: 0 })
    }
    const updated = await api.bids.update(bid.id, { selected: 1 })
    setBids((p) =>
      p.map((b) => {
        if (b.id === updated.id) return updated
        if (others.find((o) => o.id === b.id)) return { ...b, selected: 0, selected_at: null }
        return b
      })
    )
  }

  async function remove(id: string) {
    if (!window.confirm('Delete this bid?')) return
    await api.bids.delete(id)
    setBids((p) => p.filter((b) => b.id !== id))
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Bids</h2>
          <p className="muted small">
            {bids.length} bids across {byTrade.length} trade{byTrade.length === 1 ? '' : 's'}.
            Strategy Ledger §1.10 calls for 3 bids per trade minimum on Oakridge to set the
            baseline pricing database.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ Bid'}
        </button>
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Trade *</span>
            <input
              value={draft.trade ?? ''}
              onChange={(e) => setDraft({ ...draft, trade: e.target.value })}
              placeholder="framing, plumbing, etc."
            />
          </label>
          <label className="form-row">
            <span>Sub (existing)</span>
            <select
              value={draft.sub_id ?? ''}
              onChange={(e) => setDraft({ ...draft, sub_id: e.target.value || null })}
            >
              <option value="">— pick or use freeform below —</option>
              {subs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.company_name}
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span>Sub (freeform)</span>
            <input
              value={draft.sub_name_freeform ?? ''}
              onChange={(e) => setDraft({ ...draft, sub_name_freeform: e.target.value })}
              placeholder="Use only if sub isn't in the roster yet"
            />
          </label>
          <label className="form-row">
            <span>Bid amount *</span>
            <input
              type="number"
              value={(draft.bid_amount as number | undefined) ?? ''}
              onChange={(e) => setDraft({ ...draft, bid_amount: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="form-row">
            <span>Bid date</span>
            <input
              type="date"
              value={draft.bid_date ?? todayISO()}
              onChange={(e) => setDraft({ ...draft, bid_date: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Scope summary</span>
            <textarea
              rows={2}
              value={draft.scope_summary ?? ''}
              onChange={(e) => setDraft({ ...draft, scope_summary: e.target.value })}
              placeholder="What's IN this bid"
            />
          </label>
          <label className="form-row">
            <span>Exclusions</span>
            <textarea
              rows={2}
              value={draft.exclusions ?? ''}
              onChange={(e) => setDraft({ ...draft, exclusions: e.target.value })}
              placeholder="What's NOT in this bid"
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add bid
          </button>
        </div>
      )}

      {/* v2 Module 3: bid package lifecycle */}
      <div className="card mb-3" style={{ background: 'rgba(212,175,55,0.04)' }}>
        <h3 style={{ marginTop: 0 }}>Bid packages</h3>
        <p className="muted small">
          Generate a complete bid package per trade from the Master Scope + Spec Book + plans.
          Send to multiple subs with one click. AI-level the responses and award the winner.
        </p>
        {pkgError && <div className="card alert mt-2">{pkgError}</div>}
        <div className="row mt-2" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
          <input
            placeholder="Trade (e.g. framing)"
            value={pkgTrade}
            onChange={(e) => setPkgTrade(e.target.value)}
          />
          <input
            type="date"
            value={pkgDeadline}
            onChange={(e) => setPkgDeadline(e.target.value)}
            placeholder="response deadline"
          />
          <button className="btn-primary" onClick={generatePackage} disabled={pkgGenerating}>
            {pkgGenerating ? 'Generating…' : '+ Generate package'}
          </button>
        </div>
        {packages.length > 0 && (
          <div className="mt-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem' }}>
            {packages.map((p) => (
              <div
                key={p.id}
                className="card"
                style={{
                  cursor: 'pointer',
                  background: activePkg?.id === p.id ? 'rgba(212,175,55,0.18)' : 'rgba(255,255,255,0.03)',
                }}
                onClick={() => pickPackage(p.id)}
              >
                <div className="row-title" style={{ textTransform: 'capitalize' }}>{p.trade}</div>
                <div className="muted small">
                  {p.status} · {p.solicitations ?? 0} sent · {p.received ?? 0} received · {p.total_bids_for_trade ?? 0} bids
                </div>
                {p.response_deadline && (
                  <div className="muted small">Due {fmtDate(p.response_deadline)}</div>
                )}
              </div>
            ))}
          </div>
        )}
        {activePkg && (
          <div className="card mt-3">
            <h4 style={{ marginTop: 0, textTransform: 'capitalize' }}>
              {activePkg.trade} package · {activePkg.status}
            </h4>

            {/* Solicitations section */}
            <div className="mt-2">
              <strong>Solicitations</strong>
              {activeSolicitations.length === 0 ? (
                <p className="muted small">Not yet sent. Pick subs below and click Send.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {activeSolicitations.map((s) => (
                    <li key={s.id} className="small">
                      • {s.sub_name} — {s.status} {s.sent_at && `· sent ${fmtDate(s.sent_at)}`}
                    </li>
                  ))}
                </ul>
              )}
              <details className="mt-1">
                <summary className="muted small" style={{ cursor: 'pointer' }}>
                  Pick subs to send to ({sendSubIds.length} selected)
                </summary>
                <div className="mt-1" style={{ maxHeight: 180, overflow: 'auto' }}>
                  {subs
                    .filter((s) => s.email)
                    .map((s) => (
                      <label key={s.id} style={{ display: 'block', fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={sendSubIds.includes(s.id)}
                          onChange={(e) => {
                            setSendSubIds((prev) =>
                              e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id)
                            )
                          }}
                        />{' '}
                        {s.company_name} <span className="muted">({s.email})</span>
                      </label>
                    ))}
                </div>
                <button
                  className="btn-primary mt-2"
                  onClick={sendPackage}
                  disabled={pkgBusy === 'send' || sendSubIds.length === 0}
                >
                  {pkgBusy === 'send' ? 'Sending…' : `Send package to ${sendSubIds.length} subs`}
                </button>
              </details>
            </div>

            {/* Leveling */}
            <div className="mt-3">
              <strong>AI leveling</strong>
              {!activeLeveling && (
                <button
                  className="btn-primary small"
                  onClick={levelPackage}
                  disabled={pkgBusy === 'level'}
                  style={{ marginLeft: 8 }}
                >
                  {pkgBusy === 'level' ? 'Leveling…' : 'Level bids'}
                </button>
              )}
              {activeLeveling && (
                <button
                  className="btn-ghost small"
                  onClick={levelPackage}
                  disabled={pkgBusy === 'level'}
                  style={{ marginLeft: 8 }}
                >
                  Re-level
                </button>
              )}
              {activeLeveling && (
                <LevelingView
                  leveling={
                    (() => {
                      try {
                        return JSON.parse(activeLeveling.ai_analysis ?? '{}') as LevelingResultUI
                      } catch {
                        return {}
                      }
                    })()
                  }
                  onAward={awardBid}
                  awarding={pkgBusy === 'award'}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {byTrade.length === 0 && !adding && (
        <p className="muted">No bids yet. Start adding as they come in.</p>
      )}

      <h3 className="mt-3" style={{ marginBottom: 6 }}>Bids on file</h3>
      {byTrade.map(([trade, group]) => {
        const low = group[0]?.bid_amount
        const high = group[group.length - 1]?.bid_amount
        const spread = high - low
        const selected = group.find((b) => b.selected === 1)
        return (
          <div key={trade} className="card mb-3">
            <div className="row-between mb-2">
              <h3 style={{ textTransform: 'capitalize' }}>
                {trade} <span className="muted small">({group.length} bid{group.length === 1 ? '' : 's'})</span>
              </h3>
              {group.length >= 2 && (
                <span className="muted small">
                  spread {fmtMoney(spread)} · low {fmtMoney(low)} · high {fmtMoney(high)}
                </span>
              )}
            </div>
            {group.length < 3 && !selected && (
              <div className="callout small mb-2">
                Only {group.length} bid{group.length === 1 ? '' : 's'} on file. Heritage's standard
                is 3-per-trade minimum on Oakridge for the pricing baseline.
              </div>
            )}
            {group.map((b) => {
              const subName = b.sub_name ?? b.sub_name_freeform ?? '—'
              const overLow = b.bid_amount - low
              return (
                <div
                  key={b.id}
                  className="row-list"
                  style={{
                    background: b.selected === 1 ? 'rgba(111,185,138,0.06)' : undefined,
                    cursor: 'default',
                  }}
                >
                  <div className="grow">
                    <div className="row-title">
                      {subName}
                      {b.selected === 1 && (
                        <>
                          {' '}
                          <Badge color="green">SELECTED</Badge>
                        </>
                      )}
                    </div>
                    <div className="muted small">
                      {fmtDate(b.bid_date)}
                      {overLow > 0 && ` · +${fmtMoney(overLow)} over low`}
                    </div>
                    {b.scope_summary && (
                      <div className="small mt-1">
                        <strong>Includes:</strong> {b.scope_summary}
                      </div>
                    )}
                    {b.exclusions && (
                      <div className="small mt-1 muted">
                        <strong>Excludes:</strong> {b.exclusions}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="row-date">{fmtMoney(b.bid_amount)}</div>
                    <div className="row gap-2 mt-1" style={{ justifyContent: 'flex-end' }}>
                      {b.selected !== 1 && (
                        <button className="btn-ghost small" onClick={() => setSelected(b)}>
                          Select
                        </button>
                      )}
                      <button className="btn-ghost small" onClick={() => remove(b.id)}>
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}
    </section>
  )
}

function LevelingView({
  leveling,
  onAward,
  awarding,
}: {
  leveling: LevelingResultUI
  onAward: (bidId: string) => void
  awarding: boolean
}) {
  return (
    <div className="mt-2">
      {leveling.recommendation && (
        <div className="card" style={{ background: 'rgba(46,160,67,0.10)' }}>
          <strong>AI recommendation:</strong>{' '}
          {leveling.recommendation.recommended_bid_id ? (
            <span>
              {leveling.apples_to_apples?.find((a) => a.bid_id === leveling.recommendation!.recommended_bid_id)?.sub_name ?? '(unknown sub)'}
            </span>
          ) : (
            <span className="muted">no recommendation produced</span>
          )}
          <p className="small mt-1">{leveling.recommendation.reasoning}</p>
          {leveling.recommendation.risks.length > 0 && (
            <details>
              <summary className="muted small" style={{ cursor: 'pointer' }}>Risks</summary>
              <ul>
                {leveling.recommendation.risks.map((r, i) => (
                  <li key={i} className="small">{r}</li>
                ))}
              </ul>
            </details>
          )}
          {leveling.recommendation.recommended_bid_id && (
            <button
              className="btn-primary small mt-2"
              onClick={() => onAward(leveling.recommendation!.recommended_bid_id!)}
              disabled={awarding}
            >
              {awarding ? 'Awarding…' : 'Award recommended bid'}
            </button>
          )}
        </div>
      )}

      {leveling.apples_to_apples && leveling.apples_to_apples.length > 0 && (
        <div className="mt-2">
          <strong>Apples-to-apples comparison</strong>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Sub</th>
                <th style={{ textAlign: 'right' }}>Raw</th>
                <th style={{ textAlign: 'right' }}>Adjusted</th>
                <th style={{ textAlign: 'left' }}>Why</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leveling.apples_to_apples.map((a) => (
                <tr key={a.bid_id}>
                  <td>{a.sub_name}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(a.raw_amount)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(a.adjusted_amount)}</td>
                  <td>{a.adjustment_reasoning}</td>
                  <td>
                    <button className="btn-ghost small" onClick={() => onAward(a.bid_id)} disabled={awarding}>
                      Award
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {leveling.scope_gaps_against_master_scope && leveling.scope_gaps_against_master_scope.length > 0 && (
        <details className="mt-2">
          <summary className="muted small" style={{ cursor: 'pointer' }}>
            Scope gaps vs Master Scope ({leveling.scope_gaps_against_master_scope.length})
          </summary>
          <ul>
            {leveling.scope_gaps_against_master_scope.map((g, i) => (
              <li key={i} className="small">
                <strong>{g.item_description}</strong> — {g.why_no_one_bid_it}
              </li>
            ))}
          </ul>
        </details>
      )}

      {leveling.exclusions_that_become_change_orders && leveling.exclusions_that_become_change_orders.length > 0 && (
        <details className="mt-2">
          <summary className="muted small" style={{ cursor: 'pointer' }}>
            Exclusions that will become COs ({leveling.exclusions_that_become_change_orders.length})
          </summary>
          <ul>
            {leveling.exclusions_that_become_change_orders.map((e, i) => (
              <li key={i} className="small">
                {e.item}
                {e.estimated_change_order != null && (
                  <span className="muted"> · est {fmtMoney(e.estimated_change_order)}</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
